import type {
    DecryptRequest,
    DecryptResponse,
    DecryptResponseRow,
} from './decrypt.worker';
import type { PersonalField, EncryptedField } from './decryptPatientFields';

/**
 * SHER-337 — Web Worker pool for off-main-thread patient decryption.
 *
 * On CHOM (~51k patients) the census is fetched in pages of 3000 and each page
 * was decrypted synchronously on the MAIN THREAD (CryptoJS), freezing the UI
 * for ~34s total. This pool shards each page across N workers so the CPU work
 * runs in parallel off the main thread — the UI stays responsive and total
 * wall-clock drops with core count.
 *
 * Scope (Phase 1): ONLY where decryption runs changes. The decrypted output is
 * byte-for-byte identical to `decryptSinglePatientData`; the IndexedDB write
 * path is untouched.
 */

const HARD_CAP = 8;

// Per-dispatch watchdog (Blocker 3): a worker can fail in a way that fires
// neither `onmessage` nor `onerror` nor `onmessageerror` — e.g. it silently
// dies / hangs and never posts back. Without a timeout that shard's promise
// would never settle, `Promise.all` would hang forever, `decryptPatients`
// would never resolve OR reject, and `saveListPatients`'s try/catch (its only
// fallback trigger) would never run — freezing census load with no recovery,
// a failure mode the old synchronous path could not have.
//
// The timeout must be generous enough never to false-trip on a legitimately
// slow shard on weak hardware. Worst case: a full page of 3000 patients lands
// in a SINGLE worker (poolSize === 1 on a 1-core device) and each patient's
// per-field CryptoJS AES decryption is slow. Budgeting a deliberately
// pessimistic ~10ms/patient on such hardware → 30s for 3000, so we cap a base
// of 30s plus 10ms per dispatched patient and floor it at 30s. On a healthy
// CHOM login the largest shard settles in well under a second, so this only
// ever fires on a genuinely dead/hung worker.
const DISPATCH_TIMEOUT_BASE_MS = 30_000;
const DISPATCH_TIMEOUT_PER_PATIENT_MS = 10;

export interface PoolPatientInput {
    id: any;
    uuid: string;
    dateCreated: any;
    status?: number;
    personalData: EncryptedField[] | null;
}

interface PendingJob {
    resolve: (rows: DecryptResponseRow[]) => void;
    reject: (err: any) => void;
    // Watchdog timer for this dispatch (Blocker 3). Cleared on EVERY settle
    // path (onmessage / onerror / onmessageerror) so a settled job never gets
    // a late spurious rejection.
    timer: ReturnType<typeof setTimeout>;
}

class DecryptWorkerPool {
    private workers: Worker[] = [];
    private pending = new Map<number, PendingJob>();
    // requestId -> the worker it was dispatched to, so a worker error rejects
    // ONLY that worker's in-flight jobs (Blocker 2).
    private requestWorker = new Map<number, Worker>();
    private nextRequestId = 1;

    private poolSize(): number {
        const hc =
            typeof navigator !== 'undefined' && navigator.hardwareConcurrency
                ? navigator.hardwareConcurrency
                : 4;
        return Math.max(1, Math.min(hc, HARD_CAP));
    }

    // Remove a job's bookkeeping (pending entry, worker mapping, watchdog timer)
    // on any settle path. Returns the job so the caller can resolve/reject it.
    private take(requestId: number): PendingJob | undefined {
        const job = this.pending.get(requestId);
        if (job) {
            clearTimeout(job.timer);
            this.pending.delete(requestId);
            this.requestWorker.delete(requestId);
        }
        return job;
    }

    // Reject every in-flight job dispatched to `worker` with an Error (Blocker
    // 2 + the messageerror path). Scoped to THIS worker only, so one flaky
    // worker never fails the other shards; the synchronous fallback in
    // saveListPatients then degrades safely. Shared by onerror and
    // onmessageerror.
    private failWorkerJobs(worker: Worker, err: any): void {
        for (const [id, w] of this.requestWorker) {
            if (w !== worker) continue;
            const job = this.take(id);
            if (job) job.reject(err instanceof Error ? err : new Error(String(err)));
        }
    }

    private ensureWorkers(): void {
        if (this.workers.length > 0) return;
        const size = this.poolSize();
        for (let i = 0; i < size; i++) {
            const worker = new Worker(
                new URL('./decrypt.worker.ts', import.meta.url),
                { type: 'module' },
            );
            worker.onmessage = (e: MessageEvent<DecryptResponse>) => {
                const { requestId, patients } = e.data;
                const job = this.take(requestId);
                if (job) job.resolve(patients);
            };
            worker.onerror = (err) => {
                this.failWorkerJobs(worker, err);
            };
            // A `messageerror` fires when the response can't be deserialized
            // (structured-clone failure on arbitrary decrypted field values).
            // It settles NEITHER onmessage nor onerror, so without this handler
            // the shard promise would hang forever and saveListPatients' fallback
            // would never run (Blocker 3). Reject this worker's jobs so the
            // synchronous path takes over.
            worker.onmessageerror = () => {
                this.failWorkerJobs(
                    worker,
                    new Error('decrypt worker messageerror (response clone failed)'),
                );
            };
            this.workers.push(worker);
        }
    }

    private dispatch(worker: Worker, payload: DecryptRequest): Promise<DecryptResponseRow[]> {
        return new Promise<DecryptResponseRow[]>((resolve, reject) => {
            const { requestId } = payload;
            // Watchdog: if the worker silently dies / hangs and emits no event,
            // reject this job so Promise.all settles and the fallback runs.
            const timeoutMs =
                DISPATCH_TIMEOUT_BASE_MS +
                payload.patients.length * DISPATCH_TIMEOUT_PER_PATIENT_MS;
            const timer = setTimeout(() => {
                const job = this.take(requestId);
                if (job) {
                    job.reject(
                        new Error(
                            `decrypt worker timed out after ${timeoutMs}ms (requestId ${requestId})`,
                        ),
                    );
                }
            }, timeoutMs);
            this.pending.set(requestId, { resolve, reject, timer });
            this.requestWorker.set(requestId, worker);
            worker.postMessage(payload);
        });
    }

    /**
     * Shard `patients` across the pool and return them with `personalData`
     * decrypted, preserving the input order.
     */
    async decryptPatients(
        patients: PoolPatientInput[],
        personalFields: PersonalField[],
        keyInvestigation: string,
        permissions: number,
    ): Promise<DecryptResponseRow[]> {
        if (patients.length === 0) return [];
        this.ensureWorkers();

        const size = this.workers.length;
        const shards: PoolPatientInput[][] = Array.from({ length: size }, () => []);
        // Contiguous chunks keep order trivially reconstructable by concatenation.
        const chunkSize = Math.ceil(patients.length / size);
        for (let i = 0; i < size; i++) {
            const start = i * chunkSize;
            shards[i] = patients.slice(start, start + chunkSize);
        }

        const jobs = shards.map((shard, i) => {
            if (shard.length === 0) return Promise.resolve<DecryptResponseRow[]>([]);
            const payload: DecryptRequest = {
                requestId: this.nextRequestId++,
                keyInvestigation,
                permissions,
                personalFields,
                patients: shard,
            };
            return this.dispatch(this.workers[i], payload);
        });

        const results = await Promise.all(jobs);
        // Re-assemble in shard order == original order (contiguous slices).
        return results.flat();
    }
}

let singleton: DecryptWorkerPool | null = null;

export function getDecryptPool(): DecryptWorkerPool {
    if (!singleton) singleton = new DecryptWorkerPool();
    return singleton;
}

// NOTE: pool teardown on logout is deliberately NOT wired up in Phase 1
// ("must not touch logout"). The workers are idle between pages and a fresh
// login reuses the singleton. Logout teardown is deferred to Phase 2.

export type { DecryptWorkerPool };
